import type { MutationTuple } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { GENERATE_TOE_LINES_REPORT } from "../queries";
import type { ToeLinesFiltersInput } from "gql/graphql";
import { GET_NOTIFICATIONS } from "pages/dashboard/navbar/downloads/queries";
import { Logger } from "utils/logger";
import { msgError, msgSuccess } from "utils/notifications";

interface IGenerateToeLinesReportResponse {
  generateToeLinesReport: {
    success: boolean;
  };
}

interface IGenerateToeLinesReportVariables {
  groupName: string;
  verificationCode: string;
  filters?: ToeLinesFiltersInput;
}

export function useToeLineReport(
  handleClose: () => void,
): MutationTuple<
  IGenerateToeLinesReportResponse,
  IGenerateToeLinesReportVariables
>[0] {
  const { t } = useTranslation();

  const [requestGroupReport, { client }] = useMutation<
    IGenerateToeLinesReportResponse,
    IGenerateToeLinesReportVariables
  >(GENERATE_TOE_LINES_REPORT, {
    onCompleted: (): void => {
      handleClose();
      msgSuccess(
        t("groupAlerts.reportRequested"),
        t("groupAlerts.titleSuccess"),
      );
      void client.refetchQueries({ include: [GET_NOTIFICATIONS] });
    },
    onError: (errors): void => {
      errors.graphQLErrors.forEach((error): void => {
        switch (error.message) {
          case "Exception - The user already has a requested report for the same group":
            msgError(t("groupAlerts.reportAlreadyRequested"));
            break;
          case "Exception - Stakeholder could not be verified":
            msgError(t("group.findings.report.alerts.nonVerifiedStakeholder"));
            break;
          case "Exception - The verification code is invalid":
            msgError(t("group.findings.report.alerts.invalidVerificationCode"));
            break;
          default:
            msgError(t("groupAlerts.errorTextsad"));
            Logger.warning(
              "An error occurred requesting toe lines report",
              error,
            );
        }
      });
    },
  });

  return requestGroupReport;
}
